import type { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { ContactForm } from "@/components/contact-form";
import { displayFont } from "@/components/content-blocks/band-helpers";
import { ContentPageBlocks } from "@/components/content-page-blocks";
import { PageSeoHead } from "@/components/page-seo-head";
import { StorefrontIcon } from "@/components/storefront-icon";
import {
  getContentPage,
  getFooter,
  getMenuPage,
  getNavigation,
} from "@/lib/api";
import { buildStorefrontStructuredData } from "@/lib/seo-organization";
import {
  buildGoogleMapsEmbedUrl,
  buildWhatsAppUrl,
  splitContactLines,
  toExternalUrl,
} from "@/lib/contact-page";
import type {
  StorefrontLayoutVariant,
  StorefrontShellData,
} from "@/lib/storefront-shell-types";
import {
  resolveStorefrontSiteCode,
  type StorefrontSiteCode,
} from "@/lib/storefront-site";
import type {
  ContentPageData,
  FooterData,
  NavigationItem,
} from "@/lib/storefront-types";

type ContactPageProps = {
  footer: FooterData | null;
  layoutVariant: StorefrontLayoutVariant;
  navigationItems: NavigationItem[];
  page: ContentPageData;
  recaptchaSiteKey: string | null;
  shellData: StorefrontShellData;
  siteCode: StorefrontSiteCode;
};

export const getServerSideProps: GetServerSideProps<ContactPageProps> = async (
  context,
) => {
  const siteCode = resolveStorefrontSiteCode({
    configuredSiteCode: process.env.STOREFRONT_SITE,
    hostname: context.req.headers.host,
  });
  const [navigation, menuPage, page, footer] = await Promise.all([
    getNavigation(siteCode),
    getMenuPage(siteCode),
    getContentPage(["contact"], siteCode),
    getFooter(siteCode),
  ]);

  if (!page) {
    return { notFound: true };
  }

  return {
    props: {
      footer,
      layoutVariant: "immersive",
      navigationItems: navigation.items,
      page,
      recaptchaSiteKey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? null,
      siteCode,
      shellData: {
        footer,
        menuCategories: menuPage.categories,
        currentPageTitle: page.title,
        loginRegisterInsertAfter: navigation.loginRegisterInsertAfter,
      },
    },
  };
};

export default function ContactPage({
  footer,
  page,
  recaptchaSiteKey,
  siteCode,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const formIntro = page.seo.seoDescription ?? null;
  // Both brands emit their org node here (Restaurant / OnlineStore) — the
  // contact page is the natural home for ContactPoint-bearing markup.
  const structuredData = buildStorefrontStructuredData(siteCode, footer);
  const mapUrl = buildGoogleMapsEmbedUrl(footer?.contact.storeAddress ?? "");
  const whatsappUrl = buildWhatsAppUrl(footer?.contact.telephoneNumber ?? "");
  const socialLinks = footer?.socialLinks ?? [];
  const addressLines = splitContactLines(footer?.contact.storeAddress ?? "");
  const storeName = footer?.contact.storeName || "Aso Rock";

  return (
    <>
      <PageSeoHead
        siteCode={siteCode}
        seo={page.seo}
        structuredData={structuredData}
      />

      <div className="storefront-page-backdrop pb-16 sm:pb-20">
        <section className="storefront-page-divider overflow-hidden border-b">
          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(24rem,34rem)] lg:items-stretch">
            <div className="relative min-h-[18rem] overflow-hidden bg-[rgba(var(--storefront-primary-rgb),0.12)] sm:min-h-[23rem] lg:min-h-[31rem]">
              {mapUrl ? (
                <div className="absolute inset-0">
                  <iframe
                    src={mapUrl}
                    title={`${storeName} location map`}
                    className="absolute inset-0 h-full w-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              ) : (
                <div className="flex h-full min-h-[18rem] items-end bg-[rgba(var(--storefront-primary-rgb),0.12)] p-5 sm:min-h-[23rem] sm:p-6 lg:min-h-[31rem]">
                  <div className="storefront-page-panel-glass max-w-sm rounded-[1.6rem] px-5 py-5">
                    <p className="storefront-page-overline text-[0.7rem] font-semibold uppercase tracking-[0.26em]">
                      Find Us
                    </p>
                    <p className="storefront-page-title mt-3 text-base font-semibold">
                      Map unavailable right now
                    </p>
                    {addressLines.length > 0 ? (
                      <div className="storefront-page-body mt-3 space-y-1.5 text-sm leading-6">
                        {addressLines.map((line) => (
                          <p key={line}>{line}</p>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>

            <div className="storefront-page-panel-soft storefront-page-divider relative z-10 border-t px-4 py-6 sm:px-6 sm:py-8 lg:flex lg:flex-col lg:justify-between lg:border-t-0 lg:border-l lg:px-8 lg:py-10 xl:px-10">
              <div>
                <p className="storefront-page-overline text-[0.7rem] font-semibold uppercase tracking-[0.26em]">
                  Reach the store
                </p>
                <h2 className={`${displayFont.className} storefront-page-title mt-3 text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight`}>
                  {storeName}
                </h2>
                <p className="storefront-page-body mt-3 max-w-md text-[0.98rem] leading-7">
                  Call the store or use the map to plan your visit before you order or collect.
                </p>
              </div>

              <div className="mt-6 space-y-3 sm:mt-7">
                <div className="storefront-page-panel-glass rounded-[1.6rem] px-5 py-4">
                  <p className="storefront-page-overline text-[0.7rem] font-semibold uppercase tracking-[0.26em]">
                    Call
                  </p>
                  {footer?.contact.telephoneNumber ? (
                    <a
                      href={`tel:${footer.contact.telephoneNumber.replace(/\s+/g, "")}`}
                      className="storefront-page-link mt-2 inline-block text-[1.05rem] font-semibold"
                    >
                      {footer.contact.telephoneNumber}
                    </a>
                  ) : (
                    <p className="storefront-page-body-muted mt-2 text-sm">Telephone coming soon</p>
                  )}
                </div>

                <div className="storefront-page-panel-glass rounded-[1.6rem] px-5 py-4">
                  <p className="storefront-page-overline text-[0.7rem] font-semibold uppercase tracking-[0.26em]">
                    Visit
                  </p>
                  {addressLines.length > 0 ? (
                    <div className="storefront-page-body mt-2 space-y-1.5 text-sm leading-6">
                      {addressLines.map((line) => (
                        <p key={line}>{line}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="storefront-page-body-muted mt-2 text-sm">Address coming soon</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 pt-8 sm:px-6 sm:pt-10 lg:pt-12">
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] xl:gap-10">
            <div className="storefront-page-panel overflow-hidden rounded-[2rem] px-6 py-6 sm:px-8 sm:py-8">
              <ContactForm
                pagePath={page.path}
                pageEyebrow="Contact"
                pageIntro={formIntro}
                pageTitle={page.title}
                recaptchaSiteKey={recaptchaSiteKey}
              />
            </div>

            <div className="grid gap-6 lg:content-start">
              {socialLinks.length > 0 ? (
                <section className="storefront-page-panel rounded-[2rem] px-6 py-6">
                  <p className="storefront-page-overline text-[0.7rem] font-semibold uppercase tracking-[0.26em]">
                    Follow us
                  </p>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {socialLinks.map((link) => (
                      <a
                        key={`${link.platform}-${link.url}`}
                        href={toExternalUrl(link.url)}
                        target="_blank"
                        rel="noreferrer"
                        className="storefront-page-card storefront-page-card-interactive flex items-center gap-3 rounded-[1.35rem] px-4 py-3.5 text-left"
                      >
                        <span className="storefront-bg-primary inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white shadow-[0_16px_28px_-20px_rgba(0,39,0,0.6)]">
                          <StorefrontIcon
                            asset={link.icon}
                            context="social"
                            label={link.platform}
                            className="h-5 w-5"
                          />
                        </span>
                        <span className="min-w-0">
                          <span className="storefront-page-title block text-sm font-semibold uppercase tracking-[0.16em]">
                            {link.platform}
                          </span>
                          <span className="storefront-page-body-muted mt-1 block truncate text-sm">
                            Open profile
                          </span>
                        </span>
                      </a>
                    ))}
                  </div>
                </section>
              ) : null}

              {whatsappUrl ? (
                <section className="storefront-page-panel-soft rounded-[2rem] px-6 py-6">
                  <p className="storefront-page-overline text-[0.7rem] font-semibold uppercase tracking-[0.26em]">
                    Chat with us
                  </p>
                  <h2 className="storefront-page-title mt-3 text-[1.7rem] font-semibold tracking-tight">
                    Prefer a quick WhatsApp message?
                  </h2>
                  <p className="storefront-page-body mt-3 text-sm leading-6">
                    Start a chat with the store for simple questions before you
                    place an order.
                  </p>
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="storefront-page-button mt-5 inline-flex min-h-13 items-center justify-center gap-3 rounded-full px-6 text-base font-semibold"
                  >
                    <span className="inline-flex h-5 w-5 items-center justify-center">
                      <StorefrontIcon
                        context="social"
                        label="WhatsApp"
                        className="h-5 w-5"
                      />
                    </span>
                    Start WhatsApp chat
                  </a>
                </section>
              ) : null}
            </div>
          </div>
        </section>

        {page.blocks.length > 0 ? (
          <div className="mt-10 sm:mt-12">
            <ContentPageBlocks blocks={page.blocks} />
          </div>
        ) : null}
      </div>
    </>
  );
}
